import React from 'react';
import { motion } from 'framer-motion';

export interface ButtonProps {
  variant?: 'primary' | 'secondary' | 'icon' | 'quick-action';
  size?: 'sm' | 'md' | 'lg';
  children?: React.ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  icon?: React.ReactNode;
  className?: string;
}

export const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'md',
  children,
  onClick,
  disabled = false,
  icon,
  className = '',
}) => {
  const baseClasses = 'inline-flex items-center justify-center font-medium transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#D4A054] focus-visible:ring-offset-2';
  
  const sizeClasses = {
    sm: 'px-3 py-1.5 text-xs',
    md: 'px-4 py-2 text-sm',
    lg: 'px-6 py-3 text-base',
  };

  const variantClasses = {
    primary: 'bg-[#D4A054] text-white rounded-3xl hover:bg-[#B8893F] disabled:bg-[#E0E0E0] disabled:text-[#666666] shadow-[0_2px_4px_rgba(212,160,84,0.3)] hover:shadow-[0_4px_8px_rgba(212,160,84,0.4)]',
    secondary: 'bg-white text-[#333333] border border-[#E0E0E0] rounded-3xl hover:bg-[#FAFAFA] hover:border-[#D4A054] disabled:bg-[#F5F5F5] disabled:text-[#666666]',
    icon: 'w-12 h-12 rounded-full bg-white border border-[#E0E0E0] text-[#666666] hover:border-[#D4A054] hover:text-[#D4A054] disabled:bg-[#F5F5F5] disabled:text-[#999999] p-0',
    'quick-action': 'bg-white text-[#333333] rounded-[32px] px-7 py-4 shadow-[0_2px_8px_rgba(0,0,0,0.1)] hover:shadow-[0_4px_16px_rgba(0,0,0,0.12)] hover:-translate-y-0.5 disabled:bg-[#F5F5F5] disabled:text-[#666666] disabled:shadow-none disabled:transform-none gap-3',
  };

  const combinedClasses = `${baseClasses} ${variant === 'icon' ? '' : sizeClasses[size]} ${variantClasses[variant]} ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'} ${className}`;

  return (
    <motion.button
      onClick={onClick}
      disabled={disabled}
      className={combinedClasses}
      whileTap={disabled ? undefined : { scale: 0.98 }}
      transition={{ duration: 0.15 }}
    >
      {icon && variant === 'quick-action' && (
        <span className="text-[#D4A054] flex items-center justify-center">
          {icon}
        </span>
      )}
      {icon && variant === 'icon' && (
        <span className="flex items-center justify-center w-6 h-6">
          {icon}
        </span>
      )}
      {children}
    </motion.button>
  );
};

export default Button;
